import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Link } from 'react-router';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import { connectLean } from 'lean-redux';
import omit from 'lodash/omit';
import A11y from 'chameleon/ui-stack/utilities/a11y';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import {
  AssignedIconWithTooltip,
  WatchedIconWithTooltip,
} from 'chameleon/ui-stack/ui/assessments/icons-with-tooltip';
import ResourceAssign from 'chameleon-src/components/assign/ResourceAssign';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import getClassesByEP from 'chameleon-src/utility/getClassesByEP';

const DailyVideoItem = (props) => {
  const {
    location: { pathname, query },
    userData,
    withWatchedVideo = false,
    savedClasses = [],
    item,
    userClasses,
    persistAssignedSections,
    unitNumber,
    subjectData,
  } = props;
  const { highlightElement } = query;
  const { displayName, description, duration, url, id } = item;
  const { isStudent, chosenEducationPeriodCode, isAdmin, isCoordinator } =
    userData;
  const isAdminOrCoordinator = isAdmin || isCoordinator;

  const currentClasses = useMemo(
    () => getClassesByEP(userClasses, chosenEducationPeriodCode),
    [userClasses, chosenEducationPeriodCode],
  );

  useEffect(() => {
    if (highlightElement && url === highlightElement) {
      const elem = document.querySelector(`#video-${highlightElement}`);
      if (elem) {
        elem.focus();
      }
    }
  }, [highlightElement]);

  return (
    <div className="flex flex-row items-center py-6 px-3 hover:bg-blue-100 active:bg-blue-100 cursor-pointer">
      <div className="flex flex-row items-center mr-10">
        <div className="mr-4 self-start">
          <Icon
            id="daily_video_indicator"
            className="text-purple-700"
            accessibility={{ ariaHidden: true }}
          />
        </div>
        <A11y>
          <Link
            id={`video-${url}`}
            onlyActiveOnIndex={false}
            className="ml-2 hover:underline text-black"
            tabIndex={0}
            aria-label={`${displayName} ${description}`}
            to={{
              pathname,
              query: {
                ...omit(query, 'highlightElement'),
                apd: url,
              },
            }}
          >
            <span className="mr-4 font-medium text-base leading-normal">
              {displayName}
            </span>
            <span className="font-normal text-base leading-normal">
              {description}
            </span>
            {duration && (
              <span className="ml-2 text-purple-500">{duration}</span>
            )}
          </Link>
        </A11y>
      </div>

      <div className="ml-auto cursor-pointer">
        {isStudent ? (
          <div className="grid gap-y-3">
            {savedClasses.length > 0 && (
              <AssignedIconWithTooltip
                target={`daily-video-${id}-assigned`}
                className="text-gray-800"
              />
            )}
            {withWatchedVideo && (
              <WatchedIconWithTooltip
                target={`daily-video-${id}-watched`}
                className="text-gray-800"
              />
            )}
          </div>
        ) : (
          <ResourceAssign
            items={[item]}
            videoResource={{ unit: { unitId: unitNumber } }}
            subject={subjectData}
            userClasses={currentClasses}
            savedClasses={savedClasses}
            hasNoClasses={!currentClasses.length}
            isAdminOrCoordinator={isAdminOrCoordinator}
            onSubmit={(sectionIds) => {
              persistAssignedSections(
                sectionIds.map((value) => Number.parseInt(value, 10)),
              );
            }}
            assignButton={
              <ButtonWithIcon
                isIcon
                isNaked
                tabIndex={0}
                icon="person_add"
                accessibility={{
                  'aria-label': 'Assign Video',
                }}
              />
            }
            assignedButton={
              <ButtonWithIcon
                isIcon
                isSuccess
                tabIndex={0}
                icon="assigned"
                accessibility={{
                  'aria-label': 'Assigned Video',
                }}
              />
            }
          />
        )}
      </div>
    </div>
  );
};

DailyVideoItem.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  duration: PropTypes.string,
};

const mapStateToProps = ({ userData, classes: { items } }) => ({
  userData,
  userClasses: items,
});
export default compose(
  withRouter,
  connect(mapStateToProps),
  connectLean({
    scope: ({ unitNumber, item: { resourceId }, params: { subject } }) => [
      'units',
      `unit-${unitNumber}`,
      'resources',
      `resource-${resourceId}-subject-${subject}`,
    ],
    mapState: ({ withWatchedVideo, savedClasses }) => ({
      withWatchedVideo,
      savedClasses,
    }),
    persistAssignedSections(items) {
      this.setState({
        savedClasses: items,
        selectedItems: items,
      });
    },
  }),
)(DailyVideoItem);
